import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  CLUSTER_QUERY_KEY,
  VIEW_CLUSTER_QUERY_KEY,
  clusterService,
} from "../../../../../Services/clusterService";
import {
  REGION_QUERY_KEY,
  VIEW_REGION_QUERY_KEY,
  regionService,
} from "../../../../../Services/regionsService";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";

import { Region } from "../../../../../Types/MasterData/Region";
import { VIEW_DEPSITE_QUERY_KEY } from "../../../../../Services/deploymentsiteService";

interface UpdateFormProps {
  toggle?: () => void;
  cluster_id: number;
}

const UpdateClusterForm: React.FC<UpdateFormProps> = ({
  toggle,
  cluster_id,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    id: cluster_id,
    region: 0,
    company_code: "",
    name: "",
  });

  const { data: regionData, isLoading: isLoading } = useQuery<{
    data: Region[];
  }>({
    queryKey: REGION_QUERY_KEY,
    queryFn: regionService.getAllRegions,
  });

  const regions: Region[] =
    regionData?.data?.filter((item) => item.status === 1) ?? [];

  const { data: clusterData, isLoading: isFetching } = useQuery({
    queryKey: [CLUSTER_QUERY_KEY, cluster_id],
    queryFn: () => clusterService.getClusterById(cluster_id),
  });

  useEffect(() => {
    if (clusterData) {
      setFormData({
        id: clusterData.id || cluster_id,
        name: clusterData.name || "",
        region: clusterData.region || "",
        company_code: clusterData.company_code || "",
      });
    }
  }, [clusterData]);

  const updateClusterMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      clusterService.updateCluster(cluster_id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: VIEW_CLUSTER_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: VIEW_DEPSITE_QUERY_KEY });
      // queryClient.invalidateQueries();
      toast.success(result.message);
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error("Failed to update cluster.");
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectedRegion = (region: number) => {
    setFormData((prev) => ({ ...prev, region: region }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateClusterMutation.mutate(formData);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Region<b className="text-danger">*</b>
            </Label>
            <Input
              value={formData.region}
              id="region"
              name="region"
              type="select"
              disabled={isLoading}
              onChange={(e) => handleSelectedRegion(Number(e.target.value))}
            >
              <option value="" disabled>
                {isLoading ? "Loading..." : "Select Region"}
              </option>
              {!isLoading &&
                regions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="12">
            <Label>
              Name<b className="text-danger">*</b>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Loading..."
              maxLength={150}
              minLength={3}
              required
              disabled={isFetching}
              value={formData.name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={updateClusterMutation.isPending || isFetching}
            >
              {updateClusterMutation.isPending
                ? "Updating Cluster..."
                : "Update"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateClusterForm;
